import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TrendingUp, ArrowLeft, RefreshCw, Sparkles, X, RotateCcw, CheckCircle, Trash2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  generated: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function AdminDemandPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending'); // pending | generated | rejected | drafts
  const [threshold, setThreshold] = useState(2);
  const [defaultThreshold, setDefaultThreshold] = useState(2);
  const [candidates, setCandidates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [topDemand, setTopDemand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [previews, setPreviews] = useState({});

  const headers = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const setRowBusy = (key, val) => setBusy(p => ({ ...p, [key]: val }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: headers() };
      const [topRes, candRes, draftRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/search-demand/top?limit=20`, h),
        axios.get(`${API_URL}/api/admin/search-demand/candidates?limit=200`, h),
        axios.get(`${API_URL}/api/admin/search-demand/drafts?limit=200`, h),
      ]);
      setTopDemand(topRes.data.results || []);
      setDefaultThreshold(topRes.data.default_threshold ?? 2);
      setCandidates(candRes.data.candidates || []);
      setDrafts(draftRes.data.drafts || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error(`Load failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [headers, navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshCandidates = async () => {
    setRowBusy('refresh', true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/search-demand/candidates/refresh?threshold=${threshold}`,
        {},
        { headers: headers() }
      );
      toast.success(`Refreshed: +${data.inserted} new, ${data.updated} updated, ${data.skipped_terminal} skipped`);
      fetchAll();
    } catch (err) {
      toast.error(`Refresh failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy('refresh', false); }
  };

  const generate = async (dish, dryRun = false) => {
    setRowBusy(`gen-${dish}`, true);
    try {
      const url = `${API_URL}/api/admin/search-demand/candidates/${encodeURIComponent(dish)}/generate?dry_run=${dryRun ? 1 : 0}`;
      const { data } = await axios.post(url, {}, { headers: headers() });
      if (data.success && data.action === 'preview') {
        setPreviews(prev => ({ ...prev, [dish]: data.recipe }));
        toast.success(`Preview generated for review: ${data.slug}`);
      } else if (data.action === 'would_generate') {
        toast.info(`Dry run OK — ready to generate ${dish}`);
      } else if (data.reason === 'duplicate_exists') {
        toast.warning(`Duplicate exists: ${data.existing_slug}`);
      } else if (data.reason === 'candidate_not_pending') {
        toast.warning(`Candidate is ${data.current_status}, not pending`);
      } else {
        toast.error(`Generation failed: ${data.reason || 'unknown'}`);
      }
      fetchAll();
    } catch (err) {
      toast.error(`Generate failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`gen-${dish}`, false); }
  };

  const approvePreview = async (dish) => {
    const preview = previews[dish];
    if (!preview) return;
    if (!window.confirm(`Approve and save "${preview.recipe_name || dish}" to the recipe database?`)) return;

    setRowBusy(`approve-${dish}`, true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/search-demand/candidates/${encodeURIComponent(dish)}/approve-preview`,
        { recipe_json: preview, validate_schema: true },
        { headers: headers() }
      );
      setPreviews(prev => {
        const next = { ...prev };
        delete next[dish];
        return next;
      });
      toast.success(`Reviewed and saved: ${data.slug}`);
      fetchAll();
    } catch (err) {
      toast.error(`Approval failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`approve-${dish}`, false); }
  };

  const reject = async (dish) => {
    const reason = window.prompt('Reject reason (optional):', 'manual reject');
    if (reason === null) return;
    setRowBusy(`rej-${dish}`, true);
    try {
      await axios.post(
        `${API_URL}/api/admin/search-demand/candidates/${encodeURIComponent(dish)}/reject`,
        { reason },
        { headers: headers() }
      );
      toast.success(`Rejected: ${dish}`);
      fetchAll();
    } catch (err) {
      toast.error(`Reject failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`rej-${dish}`, false); }
  };

  const reopen = async (dish) => {
    setRowBusy(`reo-${dish}`, true);
    try {
      await axios.post(
        `${API_URL}/api/admin/search-demand/candidates/${encodeURIComponent(dish)}/reopen`,
        {},
        { headers: headers() }
      );
      toast.success(`Reopened: ${dish}`);
      fetchAll();
    } catch (err) {
      toast.error(`Reopen failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`reo-${dish}`, false); }
  };

  const approveDraft = async (slug) => {
    setRowBusy(`app-${slug}`, true);
    try {
      await axios.post(
        `${API_URL}/api/admin/search-demand/drafts/${encodeURIComponent(slug)}/approve`,
        {},
        { headers: headers() }
      );
      toast.success(`Published: ${slug}`);
      fetchAll();
    } catch (err) {
      toast.error(`Approve failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`app-${slug}`, false); }
  };

  const deleteDraft = async (slug) => {
    if (!window.confirm(`Delete draft "${slug}"? This cannot be undone.`)) return;
    setRowBusy(`del-${slug}`, true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/search-demand/drafts/${encodeURIComponent(slug)}`,
        { headers: headers() }
      );
      toast.success(`Deleted: ${slug}`);
      fetchAll();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    } finally { setRowBusy(`del-${slug}`, false); }
  };

  const filteredCandidates = candidates.filter(c =>
    tab === 'drafts' ? false : c.status === tab
  );

  const counts = {
    pending: candidates.filter(c => c.status === 'pending').length,
    generated: candidates.filter(c => c.status === 'generated').length,
    rejected: candidates.filter(c => c.status === 'rejected').length,
    drafts: drafts.length,
  };

  const StatusPill = ({ status }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${STATUS_STYLES[status] || ''}`}>
      {status}
    </span>
  );

  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Demand & Self-Learning - Admin</title></Helmet>
      <div className="min-h-screen bg-gray-50" data-testid="admin-demand-page">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin/recipes" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></Link>
              <TrendingUp className="w-7 h-7 text-orange-700" />
              <h1 className="text-lg font-bold text-gray-800">Self-Learning Demand</h1>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="demand-refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* Threshold control */}
          <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-4 shadow-sm" data-testid="threshold-controls">
            <div>
              <div className="text-xs text-gray-500 mb-1">Demand threshold</div>
              <Input
                type="number"
                min={1}
                value={threshold}
                onChange={e => setThreshold(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-24"
                data-testid="threshold-input"
              />
              <div className="text-[10px] text-gray-400 mt-1">Default {defaultThreshold} (env DEMAND_THRESHOLD)</div>
            </div>
            <Button onClick={refreshCandidates} disabled={!!busy.refresh} size="sm" data-testid="materialize-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Materialize Candidates
            </Button>
            <div className="text-xs text-gray-500 ml-auto">
              Top dish by demand:&nbsp;
              <span className="font-medium text-gray-800">
                {topDemand[0]?.normalized_dish || '—'}
              </span>
              {topDemand[0] && <> &middot; {topDemand[0].demand_count} hits</>}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="demand-tabs">
            {[
              { k: 'pending', label: 'Pending' },
              { k: 'generated', label: 'Generated' },
              { k: 'rejected', label: 'Rejected' },
              { k: 'drafts', label: 'Drafts' },
            ].map(t => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                  tab === t.k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`tab-${t.k}`}
              >
                {t.label} <span className="ml-1 opacity-70">({counts[t.k]})</span>
              </button>
            ))}
          </div>

          {/* Drafts panel */}
          {tab === 'drafts' && (
            <div className="space-y-2" data-testid="drafts-list">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading drafts…</div>
              ) : drafts.length === 0 ? (
                <div className="text-center py-12 text-gray-400">No demand-generated drafts.</div>
              ) : drafts.map(d => (
                <div key={d.slug} className="bg-white rounded-lg border shadow-sm p-4 flex items-start gap-3" data-testid={`draft-row-${d.slug}`}>
                  {d.image_url ? (
                    <img src={d.image_url} alt="" className="w-16 h-16 object-cover rounded-md border" />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-gray-100 flex items-center justify-center text-gray-400">
                      <FileText className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 truncate">{d.recipe_name || d.slug}</span>
                      <Badge variant="outline" className="text-[10px]">draft</Badge>
                      {d.validation_status && <Badge variant="secondary" className="text-[10px]">{d.validation_status}</Badge>}
                    </div>
                    <div className="text-xs text-gray-500 font-mono truncate">{d.slug}</div>
                    <div className="text-xs text-gray-500">
                      {d.origin_country || '—'}{d.continent ? ` · ${d.continent}` : ''}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => approveDraft(d.slug)}
                      disabled={!!busy[`app-${d.slug}`]}
                      data-testid={`approve-draft-${d.slug}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve & Publish
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => navigate(`/admin/recipes/${d.slug}/edit`)}
                        data-testid={`edit-draft-${d.slug}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                        onClick={() => deleteDraft(d.slug)}
                        disabled={!!busy[`del-${d.slug}`]}
                        data-testid={`delete-draft-${d.slug}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Candidates panel */}
          {tab !== 'drafts' && (
            <div className="space-y-2" data-testid="candidates-list">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading…</div>
              ) : filteredCandidates.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  {tab === 'pending'
                    ? 'No pending candidates. Click "Materialize Candidates" to scan demand events.'
                    : `No ${tab} candidates.`}
                </div>
              ) : filteredCandidates.map(c => (
                <div key={c.normalized_dish} className="bg-white rounded-lg border shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid={`candidate-row-${c.normalized_dish}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 truncate">{c.normalized_dish}</span>
                      <StatusPill status={c.status} />
                      <Badge variant="secondary" className="text-[10px]">demand: {c.demand_count}</Badge>
                      {c.languages?.length > 0 && (
                        <Badge variant="outline" className="text-[10px]">{c.languages.join(', ')}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Last seen: {c.last_seen ? new Date(c.last_seen).toLocaleString() : '—'}
                    </div>
                    {c.generated_slug && (
                      <div className="text-xs text-emerald-700 mt-0.5">→ {c.generated_slug}</div>
                    )}
                    {c.reject_reason && (
                      <div className="text-xs text-rose-700 mt-0.5">Reason: {c.reject_reason}</div>
                    )}
                    {previews[c.normalized_dish] && (
                      <div className="mt-2 rounded border border-violet-200 bg-violet-50 p-3 text-xs">
                        <div className="font-medium text-violet-900">
                          AI preview: {previews[c.normalized_dish].recipe_name || c.normalized_dish}
                        </div>
                        <div className="text-violet-700">
                          {previews[c.normalized_dish].origin_country || 'Unknown origin'} · Not saved
                        </div>
                        <details className="mt-2 text-gray-700">
                          <summary className="cursor-pointer font-medium text-violet-900">
                            Review full preview
                          </summary>
                          <div className="mt-2 grid gap-3 md:grid-cols-2">
                            <div>
                              <div className="font-semibold">Ingredients</div>
                              <ul className="list-disc pl-4">
                                {(previews[c.normalized_dish].ingredients || []).map((ingredient, index) => (
                                  <li key={index}>
                                    {typeof ingredient === 'string'
                                      ? ingredient
                                      : [ingredient.amount, ingredient.unit, ingredient.item].filter(Boolean).join(' ')}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="font-semibold">Instructions</div>
                              <ol className="list-decimal pl-4">
                                {(previews[c.normalized_dish].instructions || []).map((step, index) => (
                                  <li key={index}>
                                    {typeof step === 'string' ? step : step.instruction}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {c.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => generate(c.normalized_dish, false)}
                          disabled={!!busy[`gen-${c.normalized_dish}`]}
                          data-testid={`generate-${c.normalized_dish}`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          {busy[`gen-${c.normalized_dish}`] ? 'Generating…' : 'Generate Preview'}
                        </Button>
                        {previews[c.normalized_dish] && (
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800"
                            onClick={() => approvePreview(c.normalized_dish)}
                            disabled={!!busy[`approve-${c.normalized_dish}`]}
                            data-testid={`approve-preview-${c.normalized_dish}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Review & Save
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => reject(c.normalized_dish)}
                          disabled={!!busy[`rej-${c.normalized_dish}`]}
                          data-testid={`reject-${c.normalized_dish}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {c.status === 'rejected' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => reopen(c.normalized_dish)}
                        disabled={!!busy[`reo-${c.normalized_dish}`]}
                        data-testid={`reopen-${c.normalized_dish}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                      </Button>
                    )}
                    {c.status === 'generated' && c.generated_slug && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => navigate(`/admin/recipes/${c.generated_slug}/edit`)}
                        data-testid={`view-${c.normalized_dish}`}
                      >
                        View Recipe
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
